import React, { useEffect, useState, useCallback, useMemo } from 'react';
import NavHeader from '../components/NavHeader';
import MicrophoneButton from '../components/MicrophoneButton';
import {
  fetchProjects,
  fetchUsers,
  fetchVendors,
  createProject,
  updateProject,
  deleteProject,
  getProjectVendorMappingsByProject,
  createProjectVendorMapping,
  updateProjectVendorMapping,
  deleteProjectVendorMapping,
  createMappingInstruction,
  updateMappingInstruction,
  deleteMappingInstruction,
  createMappingKeyword,
  deleteMappingKeyword,
  checkKeywordDuplicate,
  fetchVendorAliases,
  createVendorAlias,
  deleteVendorAlias,
} from '../api';

const EMPTY_FORM = {
  name: '',
  owner_id: '',
  status: 'active',
  default_gl_company_code: '',
  default_gl_cost_center: '',
  default_gl_internal_order: '',
  default_gl_account: '',
  ai_classification_instructions: '',
  area: '',
  default_jurisdiction: '',
};

function ProjectsAdmin() {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // Modal state — null = closed, 'add' = new project, UUID = editing that project
  const [modalMode, setModalMode] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Vendor mappings state for the modal
  const [projectMappings, setProjectMappings] = useState([]);
  const [mappingsLoading, setMappingsLoading] = useState(false);
  const [expandedMappingId, setExpandedMappingId] = useState(null);

  // Add mapping form within modal
  const [newMappingVendorId, setNewMappingVendorId] = useState('');
  const [newMappingGlAccount, setNewMappingGlAccount] = useState('');
  const [newMappingGlCostCenter, setNewMappingGlCostCenter] = useState('');
  const [addingMapping, setAddingMapping] = useState(false);

  // Add instruction form
  const [newInstructionText, setNewInstructionText] = useState('');
  const [newInstructionWeight, setNewInstructionWeight] = useState(10);
  const [addingInstruction, setAddingInstruction] = useState(false);

  // Edit instruction state
  const [editingInstructionId, setEditingInstructionId] = useState(null);
  const [editInstructionText, setEditInstructionText] = useState('');
  const [editInstructionWeight, setEditInstructionWeight] = useState(10);

  // Keyword state
  const [newKeywordTexts, setNewKeywordTexts] = useState({});
  const [keywordDuplicateWarnings, setKeywordDuplicateWarnings] = useState({});

  // Vendor alias state
  const [newAliasTexts, setNewAliasTexts] = useState({});
  const [vendorAliases, setVendorAliases] = useState({});

  const showToast = useCallback((message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [projectsRes, usersRes, vendorsRes] = await Promise.all([
        fetchProjects(),
        fetchUsers(),
        fetchVendors(),
      ]);
      const pData = projectsRes.data;
      setProjects(Array.isArray(pData) ? pData : (pData?.results ?? []));
      const uData = usersRes.data;
      setUsers(Array.isArray(uData) ? uData : (uData?.results ?? []));
      const vData = vendorsRes.data;
      setVendors(Array.isArray(vData) ? vData : (vData?.results ?? []));
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const userMap = {};
  users.forEach((u) => (userMap[u.id] = u));

  const filteredProjects = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return projects
      .filter(p => {
        if (!term) return true;
        const ownerName = users.find(u => u.id === p.owner_id)?.name || '';
        return [p.name, p.area, p.default_gl_internal_order, p.default_jurisdiction, ownerName]
          .some(field => (field || '').toLowerCase().includes(term));
      })
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [projects, searchTerm, users]);

  const loadProjectMappings = async (projectId) => {
    setMappingsLoading(true);
    try {
      const res = await getProjectVendorMappingsByProject(projectId);
      const data = res.data;
      setProjectMappings(Array.isArray(data) ? data : (data?.results ?? []));
    } catch (err) {
      showToast('Failed to load vendor mappings');
    } finally {
      setMappingsLoading(false);
    }
  };

  const openAdd = () => {
    setFormData({ ...EMPTY_FORM });
    setProjectMappings([]);
    setExpandedMappingId(null);
    setModalMode('add');
  };

  const openEdit = (project) => {
    setFormData({
      name: project.name || '',
      owner_id: project.owner_id || '',
      status: project.status || 'active',
      default_gl_company_code: project.default_gl_company_code || '',
      default_gl_cost_center: project.default_gl_cost_center || '',
      default_gl_internal_order: project.default_gl_internal_order || '',
      default_gl_account: project.default_gl_account || '',
      ai_classification_instructions: project.ai_classification_instructions || '',
      area: project.area || '',
      default_jurisdiction: project.default_jurisdiction || '',
    });
    setExpandedMappingId(null);
    setModalMode(project.id);
    loadProjectMappings(project.id);
  };

  const closeModal = () => {
    setModalMode(null);
    setFormData({ ...EMPTY_FORM });
    setProjectMappings([]);
    setExpandedMappingId(null);
    setNewMappingVendorId('');
    setNewMappingGlAccount('');
    setNewMappingGlCostCenter('');
    setNewInstructionText('');
    setNewInstructionWeight(10);
    setEditingInstructionId(null);
    setNewKeywordTexts({});
    setKeywordDuplicateWarnings({});
    setNewAliasTexts({});
    setVendorAliases({});
  };

  const handleSave = async () => {
    if (!formData.name || !formData.owner_id) return;
    setSaving(true);
    try {
      if (modalMode === 'add') {
        const payload = { ...formData };
        if (!payload.default_gl_company_code) delete payload.default_gl_company_code;
        if (!payload.default_gl_cost_center) delete payload.default_gl_cost_center;
        if (!payload.default_gl_internal_order) delete payload.default_gl_internal_order;
        if (!payload.default_gl_account) delete payload.default_gl_account;
        if (!payload.ai_classification_instructions) delete payload.ai_classification_instructions;
        if (!payload.area) delete payload.area;
        if (!payload.default_jurisdiction) delete payload.default_jurisdiction;
        await createProject(payload);
        showToast('Project created successfully', 'success');
      } else {
        const payload = { ...formData };
        if (!payload.default_gl_company_code) payload.default_gl_company_code = null;
        if (!payload.default_gl_cost_center) payload.default_gl_cost_center = null;
        if (!payload.default_gl_internal_order) payload.default_gl_internal_order = null;
        if (!payload.default_gl_account) payload.default_gl_account = null;
        if (!payload.ai_classification_instructions) payload.ai_classification_instructions = null;
        if (!payload.area) payload.area = null;
        if (!payload.default_jurisdiction) payload.default_jurisdiction = null;
        await updateProject(modalMode, payload);
        showToast('Project updated successfully', 'success');
      }
      closeModal();
      loadData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (e, projectId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await deleteProject(projectId);
      showToast('Project deleted', 'success');
      loadData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to delete project');
    }
  };

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // --- Vendor Mapping handlers ---
  const handleAddMapping = async () => {
    if (!newMappingVendorId || modalMode === 'add') return;
    setAddingMapping(true);
    try {
      await createProjectVendorMapping(
        modalMode,
        newMappingVendorId,
        newMappingGlAccount || null,
        newMappingGlCostCenter || null
      );
      setNewMappingVendorId('');
      setNewMappingGlAccount('');
      setNewMappingGlCostCenter('');
      showToast('Vendor mapping added', 'success');
      loadProjectMappings(modalMode);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to add mapping');
    } finally {
      setAddingMapping(false);
    }
  };

  const handleUpdateMappingGl = async (mappingId, field, value) => {
    try {
      await updateProjectVendorMapping(mappingId, { [field]: value || null });
      loadProjectMappings(modalMode);
    } catch (err) {
      showToast('Failed to update mapping');
    }
  };

  const handleDeleteMapping = async (mappingId) => {
    if (!window.confirm('Delete this vendor mapping and all its AI instructions?')) return;
    try {
      await deleteProjectVendorMapping(mappingId);
      showToast('Mapping deleted', 'success');
      if (expandedMappingId === mappingId) setExpandedMappingId(null);
      loadProjectMappings(modalMode);
    } catch (err) {
      showToast('Failed to delete mapping');
    }
  };

  // --- Instruction handlers ---
  const handleAddInstruction = async (mappingId) => {
    if (!newInstructionText.trim()) return;
    setAddingInstruction(true);
    try {
      await createMappingInstruction(mappingId, newInstructionText.trim(), newInstructionWeight);
      setNewInstructionText('');
      setNewInstructionWeight(10);
      showToast('Instruction added', 'success');
      loadProjectMappings(modalMode);
    } catch (err) {
      showToast('Failed to add instruction');
    } finally {
      setAddingInstruction(false);
    }
  };

  const startEditInstruction = (instruction) => {
    setEditingInstructionId(instruction.id);
    setEditInstructionText(instruction.instruction_text);
    setEditInstructionWeight(instruction.weight);
  };

  const handleSaveInstruction = async (mappingId, instructionId) => {
    try {
      await updateMappingInstruction(mappingId, instructionId, {
        instruction_text: editInstructionText,
        weight: editInstructionWeight,
      });
      setEditingInstructionId(null);
      showToast('Instruction updated', 'success');
      loadProjectMappings(modalMode);
    } catch (err) {
      showToast('Failed to update instruction');
    }
  };

  const handleDeleteInstruction = async (mappingId, instructionId) => {
    try {
      await deleteMappingInstruction(mappingId, instructionId);
      showToast('Instruction deleted', 'success');
      loadProjectMappings(modalMode);
    } catch (err) {
      showToast('Failed to delete instruction');
    }
  };

  // --- Keyword handlers ---
  const handleAddKeyword = async (mappingId) => {
    const text = (newKeywordTexts[mappingId] || '').trim();
    if (!text) return;
    try {
      await createMappingKeyword(mappingId, text);
      setNewKeywordTexts(prev => ({ ...prev, [mappingId]: '' }));
      setKeywordDuplicateWarnings(prev => ({ ...prev, [mappingId]: null }));
      showToast('Keyword added', 'success');
      loadProjectMappings(modalMode);
    } catch (err) {
      if (err.response?.status === 409) {
        showToast('This keyword already exists on this mapping', 'error');
      } else {
        showToast('Failed to add keyword', 'error');
      }
    }
  };

  const handleDeleteKeyword = async (mappingId, keywordId) => {
    try {
      await deleteMappingKeyword(mappingId, keywordId);
      showToast('Keyword removed', 'success');
      loadProjectMappings(modalMode);
    } catch (err) {
      showToast('Failed to remove keyword', 'error');
    }
  };

  const handleCheckKeywordDuplicate = async (mappingId, keyword) => {
    if (!keyword.trim()) {
      setKeywordDuplicateWarnings(prev => ({ ...prev, [mappingId]: null }));
      return;
    }
    try {
      const res = await checkKeywordDuplicate(mappingId, keyword.trim());
      if (res.data.duplicates && res.data.duplicates.length > 0) {
        const names = res.data.duplicates.map(d => d.project_name).join(', ');
        setKeywordDuplicateWarnings(prev => ({
          ...prev,
          [mappingId]: `Warning: This keyword also exists on: ${names}`
        }));
      } else {
        setKeywordDuplicateWarnings(prev => ({ ...prev, [mappingId]: null }));
      }
    } catch {
      setKeywordDuplicateWarnings(prev => ({ ...prev, [mappingId]: null }));
    }
  };

  // --- Vendor Alias handlers ---
  const loadVendorAliases = async (vendorId) => {
    try {
      const res = await fetchVendorAliases(vendorId);
      setVendorAliases(prev => ({ ...prev, [vendorId]: res.data }));
    } catch (err) {
      console.error('Failed to load aliases', err);
    }
  };

  const handleAddAlias = async (vendorId) => {
    const text = (newAliasTexts[vendorId] || '').trim();
    if (!text) return;
    try {
      await createVendorAlias(vendorId, text);
      setNewAliasTexts(prev => ({ ...prev, [vendorId]: '' }));
      showToast('Alias added', 'success');
      loadVendorAliases(vendorId);
    } catch (err) {
      if (err.response?.status === 409) {
        showToast('This alias is already in use by another vendor', 'error');
      } else {
        showToast('Failed to add alias', 'error');
      }
    }
  };

  const handleDeleteAlias = async (vendorId, aliasId) => {
    try {
      await deleteVendorAlias(vendorId, aliasId);
      showToast('Alias removed', 'success');
      loadVendorAliases(vendorId);
    } catch (err) {
      showToast('Failed to remove alias', 'error');
    }
  };

  // Vendors already used in this project's mappings
  const usedVendorIds = new Set(projectMappings.map(m => m.vendor_id));
  const availableVendors = vendors.filter(v => !usedVendorIds.has(v.id));

  return (
    <div className="projects-page">
      <NavHeader actions={
        <button className="btn btn-primary" onClick={openAdd}>Add Project</button>
      } />

      {/* Toast */}
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* Search */}
      <div className="projects-search">
        <input
          type="text"
          placeholder="Search projects by name, area, internal order, owner..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {searchTerm && (
          <span className="search-count">{filteredProjects.length} of {projects.length} projects</span>
        )}
      </div>

      {/* Projects Table */}
      <div className="projects-table-container">
        {loading ? (
          <div className="loading-spinner">Loading projects...</div>
        ) : (
          <>
            <table className="projects-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Owner</th>
                  <th>Status</th>
                  <th>Area</th>
                  <th>Jurisdiction</th>
                  <th>GL Company Code</th>
                  <th>GL Cost Center</th>
                  <th>GL Account</th>
                  <th>GL Internal Order</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map((p) => (
                  <tr
                    key={p.id}
                    className="clickable-row"
                    onClick={() => openEdit(p)}
                  >
                    <td>{p.name}</td>
                    <td>{userMap[p.owner_id]?.name || p.owner_id}</td>
                    <td>
                      <span className={`status-pill status-${p.status}`}>
                        {p.status === 'active' ? 'Active' : 'Closed'}
                      </span>
                    </td>
                    <td>{p.area || '\u2014'}</td>
                    <td>{p.default_jurisdiction || '\u2014'}</td>
                    <td>{p.default_gl_company_code || '\u2014'}</td>
                    <td>{p.default_gl_cost_center || '\u2014'}</td>
                    <td>{p.default_gl_account || '\u2014'}</td>
                    <td>{p.default_gl_internal_order || '\u2014'}</td>
                    <td className="project-actions">
                      <button
                        className="btn btn-reject btn-sm"
                        onClick={(e) => handleDelete(e, p.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredProjects.length === 0 && searchTerm && (
              <div className="projects-empty">No matching projects.</div>
            )}
            {projects.length === 0 && !searchTerm && (
              <div className="projects-empty">No projects yet. Click "Add Project" above.</div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {modalMode !== null && (
        <div className="project-modal-overlay" onClick={closeModal}>
          <div className="project-modal project-modal--wide" onClick={(e) => e.stopPropagation()}>
            <div className="project-modal-header">
              <h2>{modalMode === 'add' ? 'New Project' : 'Edit Project'}</h2>
              <button className="project-modal-close" onClick={closeModal}>&times;</button>
            </div>

            <div className="project-modal-body">
              <div className="project-modal-row">
                <label>Project Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder="Project name"
                  autoFocus
                />
              </div>

              <div className="project-modal-row">
                <label>Owner *</label>
                <select
                  value={formData.owner_id}
                  onChange={(e) => setField('owner_id', e.target.value)}
                >
                  <option value="">Select Owner</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>

              <div className="project-modal-row">
                <label>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setField('status', e.target.value)}
                >
                  <option value="active">Active</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div className="project-modal-row-group">
                <div className="project-modal-row">
                  <label>Area</label>
                  <input
                    type="text"
                    value={formData.area}
                    onChange={(e) => setField('area', e.target.value)}
                    placeholder="e.g. Tax, Legal, Corporate"
                  />
                </div>
                <div className="project-modal-row">
                  <label>Default Jurisdiction</label>
                  <input
                    type="text"
                    value={formData.default_jurisdiction}
                    onChange={(e) => setField('default_jurisdiction', e.target.value)}
                    placeholder="e.g. US, LU, DE"
                  />
                </div>
                <div className="project-modal-row">
                  <label>GL Company Code</label>
                  <input
                    type="text"
                    value={formData.default_gl_company_code}
                    onChange={(e) => setField('default_gl_company_code', e.target.value)}
                    placeholder="e.g. 1000"
                  />
                </div>
              </div>

              <div className="project-modal-row-group">
                <div className="project-modal-row">
                  <label>GL Cost Center</label>
                  <input
                    type="text"
                    value={formData.default_gl_cost_center}
                    onChange={(e) => setField('default_gl_cost_center', e.target.value)}
                    placeholder="e.g. CC-4200"
                  />
                </div>
                <div className="project-modal-row">
                  <label>GL Account</label>
                  <input
                    type="text"
                    value={formData.default_gl_account}
                    onChange={(e) => setField('default_gl_account', e.target.value)}
                    placeholder="e.g. 600100"
                  />
                </div>
                <div className="project-modal-row">
                  <label>GL Internal Order</label>
                  <input
                    type="text"
                    value={formData.default_gl_internal_order}
                    onChange={(e) => setField('default_gl_internal_order', e.target.value)}
                    placeholder="e.g. IO-80001"
                  />
                </div>
              </div>

              {/* Vendor Mappings Section */}
              <div className="modal-mappings-section">
                <div className="modal-mappings-header">
                  <h3>Vendor Mappings</h3>
                </div>

                {modalMode === 'add' ? (
                  <p className="modal-mappings-note">
                    Save the project first, then edit to add vendor mappings.
                  </p>
                ) : (
                  <>
                    {mappingsLoading ? (
                      <div className="loading-spinner" style={{ padding: '12px 0' }}>Loading mappings...</div>
                    ) : (
                      <>
                        {projectMappings.length > 0 && (
                          <table className="modal-mappings-table">
                            <thead>
                              <tr>
                                <th>Vendor</th>
                                <th>GL Account</th>
                                <th>GL Cost Center</th>
                                <th>Source</th>
                                <th style={{ width: 100 }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {projectMappings.map((m) => (
                                <React.Fragment key={m.id}>
                                  <tr className="modal-mapping-row">
                                    <td>{m.vendor_name}</td>
                                    <td>
                                      <input
                                        type="text"
                                        value={m.gl_account || ''}
                                        onChange={() => {}}
                                        onBlur={(e) => {
                                          if (e.target.value !== (m.gl_account || '')) {
                                            handleUpdateMappingGl(m.id, 'gl_account', e.target.value);
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') e.target.blur();
                                        }}
                                        defaultValue={m.gl_account || ''}
                                        placeholder="\u2014"
                                        className="inline-input"
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="text"
                                        value={m.gl_cost_center || ''}
                                        onChange={() => {}}
                                        onBlur={(e) => {
                                          if (e.target.value !== (m.gl_cost_center || '')) {
                                            handleUpdateMappingGl(m.id, 'gl_cost_center', e.target.value);
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') e.target.blur();
                                        }}
                                        defaultValue={m.gl_cost_center || ''}
                                        placeholder="\u2014"
                                        className="inline-input"
                                      />
                                    </td>
                                    <td>
                                      <span className={`source-badge source-${m.source}`}>{m.source}</span>
                                    </td>
                                    <td>
                                      <div style={{ display: 'flex', gap: '4px' }}>
                                        <button
                                          className="btn btn-secondary btn-sm"
                                          onClick={() => {
                                            const expanding = expandedMappingId !== m.id;
                                            setExpandedMappingId(expanding ? m.id : null);
                                            if (expanding) loadVendorAliases(m.vendor_id);
                                          }}
                                          title="Keywords &amp; AI Instructions"
                                        >
                                          {expandedMappingId === m.id ? 'Hide' : 'Config'} ({(m.keywords || []).length}K / {(m.instructions || []).length}I)
                                        </button>
                                        <button
                                          className="btn btn-reject btn-sm"
                                          onClick={() => handleDeleteMapping(m.id)}
                                        >
                                          Del
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                  {/* Expanded instructions */}
                                  {expandedMappingId === m.id && (
                                    <tr>
                                      <td colSpan={5} className="instructions-cell">
                                        {/* Vendor Aliases */}
                                        <div className="aliases-section">
                                          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                                            Vendor Aliases
                                            <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                                              (other names this vendor appears as on invoices)
                                            </span>
                                          </div>
                                          <div className="keyword-chips">
                                            {(vendorAliases[m.vendor_id] || []).map((alias) => (
                                              <span key={alias.id} className="keyword-chip">
                                                <span className="keyword-chip-text">{alias.alias_name}</span>
                                                <button
                                                  className="keyword-delete-btn"
                                                  onClick={() => handleDeleteAlias(m.vendor_id, alias.id)}
                                                  title="Remove alias"
                                                >
                                                  &times;
                                                </button>
                                              </span>
                                            ))}
                                            {(vendorAliases[m.vendor_id] || []).length === 0 && (
                                              <span style={{ fontSize: '0.85em', color: '#999' }}>No aliases yet</span>
                                            )}
                                          </div>
                                          <div className="keyword-add-row">
                                            <input
                                              type="text"
                                              className="instruction-text-input"
                                              placeholder="Add alias name..."
                                              value={newAliasTexts[m.vendor_id] || ''}
                                              onChange={(e) => setNewAliasTexts(prev => ({ ...prev, [m.vendor_id]: e.target.value }))}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter' && (newAliasTexts[m.vendor_id] || '').trim()) {
                                                  handleAddAlias(m.vendor_id);
                                                }
                                              }}
                                            />
                                            <button
                                              className="btn btn-primary btn-sm"
                                              onClick={() => handleAddAlias(m.vendor_id)}
                                              disabled={!(newAliasTexts[m.vendor_id] || '').trim()}
                                            >
                                              Add
                                            </button>
                                          </div>
                                        </div>

                                        {/* Keywords section */}
                                        <div className="keywords-section">
                                          <div className="instructions-title">
                                            Keywords <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>for deterministic matching</span>
                                          </div>
                                          <div className="keyword-chips">
                                            {(m.keywords || []).map((kw) => (
                                              <span key={kw.id} className="keyword-chip">
                                                <span className="keyword-chip-text">{kw.keyword}</span>
                                                {kw.times_matched > 0 && (
                                                  <span className="keyword-matched-count">{kw.times_matched}</span>
                                                )}
                                                <button
                                                  className="keyword-delete-btn"
                                                  onClick={() => handleDeleteKeyword(m.id, kw.id)}
                                                  title="Remove keyword"
                                                >
                                                  &times;
                                                </button>
                                              </span>
                                            ))}
                                            {(m.keywords || []).length === 0 && (
                                              <span style={{ fontSize: '0.85em', color: '#999' }}>No keywords yet</span>
                                            )}
                                          </div>
                                          <div className="keyword-add-row">
                                            <input
                                              type="text"
                                              className="instruction-text-input"
                                              placeholder="Add keyword..."
                                              value={newKeywordTexts[m.id] || ''}
                                              onChange={(e) => setNewKeywordTexts(prev => ({ ...prev, [m.id]: e.target.value }))}
                                              onBlur={(e) => handleCheckKeywordDuplicate(m.id, e.target.value)}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter' && (newKeywordTexts[m.id] || '').trim()) {
                                                  handleAddKeyword(m.id);
                                                }
                                              }}
                                            />
                                            <button
                                              className="btn btn-primary btn-sm"
                                              onClick={() => handleAddKeyword(m.id)}
                                              disabled={!(newKeywordTexts[m.id] || '').trim()}
                                            >
                                              Add
                                            </button>
                                          </div>
                                          {keywordDuplicateWarnings[m.id] && (
                                            <div className="keyword-duplicate-warning">
                                              {keywordDuplicateWarnings[m.id]}
                                            </div>
                                          )}
                                        </div>

                                        {/* AI Instructions section */}
                                        <div className="instructions-section">
                                          <div className="instructions-title">AI Instructions for {m.vendor_name}</div>
                                          {(m.instructions || []).map((inst) => (
                                            <div key={inst.id} className="instruction-row">
                                              {editingInstructionId === inst.id ? (
                                                <>
                                                  <input
                                                    type="text"
                                                    className="instruction-text-input"
                                                    value={editInstructionText}
                                                    onChange={(e) => setEditInstructionText(e.target.value)}
                                                  />
                                                  <MicrophoneButton
                                                    onTranscribed={(text) => setEditInstructionText((prev) => (prev ? `${prev} ${text}` : text))}
                                                  />
                                                  <div className="instruction-weight">
                                                    <input
                                                      type="range"
                                                      min="1"
                                                      max="10"
                                                      value={editInstructionWeight}
                                                      onChange={(e) => setEditInstructionWeight(parseInt(e.target.value))}
                                                      className="weight-slider"
                                                    />
                                                    <span className="weight-value">{editInstructionWeight}</span>
                                                  </div>
                                                  <button className="btn btn-primary btn-sm" onClick={() => handleSaveInstruction(m.id, inst.id)}>Save</button>
                                                  <button className="btn btn-secondary btn-sm" onClick={() => setEditingInstructionId(null)}>Cancel</button>
                                                </>
                                              ) : (
                                                <>
                                                  <span className="instruction-text">{inst.instruction_text}</span>
                                                  <div className="instruction-weight">
                                                    <span className="weight-label">Weight:</span>
                                                    <span className="weight-value">{inst.weight}/10</span>
                                                  </div>
                                                  <button className="btn btn-secondary btn-sm" onClick={() => startEditInstruction(inst)}>Edit</button>
                                                  <button className="btn btn-reject btn-sm" onClick={() => handleDeleteInstruction(m.id, inst.id)}>Del</button>
                                                </>
                                              )}
                                            </div>
                                          ))}
                                          {/* Add instruction form */}
                                          <div className="instruction-add-row">
                                            <input
                                              type="text"
                                              className="instruction-text-input"
                                              placeholder="Enter AI instruction..."
                                              value={newInstructionText}
                                              onChange={(e) => setNewInstructionText(e.target.value)}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter' && newInstructionText.trim()) {
                                                  handleAddInstruction(m.id);
                                                }
                                              }}
                                            />
                                            <MicrophoneButton
                                              onTranscribed={(text) => setNewInstructionText((prev) => (prev ? `${prev} ${text}` : text))}
                                            />
                                            <div className="instruction-weight">
                                              <input
                                                type="range"
                                                min="1"
                                                max="10"
                                                value={newInstructionWeight}
                                                onChange={(e) => setNewInstructionWeight(parseInt(e.target.value))}
                                                className="weight-slider"
                                              />
                                              <span className="weight-value">{newInstructionWeight}</span>
                                            </div>
                                            <button
                                              className="btn btn-primary btn-sm"
                                              onClick={() => handleAddInstruction(m.id)}
                                              disabled={!newInstructionText.trim() || addingInstruction}
                                            >
                                              {addingInstruction ? '...' : 'Add'}
                                            </button>
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        )}

                        {projectMappings.length === 0 && (
                          <p className="modal-mappings-note">No vendor mappings yet.</p>
                        )}

                        {/* Add mapping row */}
                        <div className="modal-add-mapping-row">
                          <select
                            value={newMappingVendorId}
                            onChange={(e) => setNewMappingVendorId(e.target.value)}
                          >
                            <option value="">Select Vendor...</option>
                            {availableVendors.map(v => (
                              <option key={v.id} value={v.id}>{v.name}</option>
                            ))}
                          </select>
                          <input
                            type="text"
                            placeholder="GL Account"
                            value={newMappingGlAccount}
                            onChange={(e) => setNewMappingGlAccount(e.target.value)}
                          />
                          <input
                            type="text"
                            placeholder="GL Cost Center"
                            value={newMappingGlCostCenter}
                            onChange={(e) => setNewMappingGlCostCenter(e.target.value)}
                          />
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={handleAddMapping}
                            disabled={!newMappingVendorId || addingMapping}
                          >
                            {addingMapping ? '...' : 'Add Vendor'}
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="project-modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={!formData.name || !formData.owner_id || saving}
              >
                {saving ? 'Saving...' : (modalMode === 'add' ? 'Create Project' : 'Save Changes')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectsAdmin;
